/**
 * AfiTimingExpectations — AFI-G2
 *
 * Onboarding interstitial shown once between step 5 completion and
 * the final dashboard redirect. Sets accurate expectations about
 * per-pillar movement timescales.
 *
 * Voice: diagnostic, calm (Brand Philosophy v1.1). No "Don't worry!"
 * framing — customers feel calibrated, not managed.
 */

import { PROJECTION_WINDOWS, PROJECTION_DISCLAIMER } from '@fixmyseo/shared/projection-windows';

interface Props {
  onContinue: () => void;
}

export function AfiTimingExpectations({ onContinue }: Props) {
  return (
    <div className="max-w-xl">
      <h1 className="text-2xl font-bold text-brand-ink tracking-tight mb-3">
        Before you dive in: how FixMySEO works on different timescales
      </h1>

      <p className="text-sm text-brand-muted leading-relaxed mb-8">
        Your AI visibility moves on three different timescales depending on the pillar. When FixMySEO
        deploys a fix, the SEO sub-score moves quickly, AEO follows within a week, and GEO accumulates
        over weeks. Knowing this helps you evaluate progress correctly.
      </p>

      <div className="flex flex-col gap-4 mb-8">
        {Object.values(PROJECTION_WINDOWS).map((pw) => (
          <article
            key={pw.pillar}
            className="flex items-start gap-5 bg-white rounded-xl border border-gray-100 p-5"
          >
            <div className="flex flex-col items-center shrink-0 gap-1 pt-0.5">
              <PillarBadge pillar={pw.pillar} label={pw.pillarLabel} />
              <span className="text-base font-bold text-brand-ink whitespace-nowrap tabular-nums">
                {pw.displayLabel}
              </span>
            </div>
            <p className="text-sm text-brand-muted leading-relaxed m-0">
              {pw.detailedDescription}
            </p>
          </article>
        ))}
      </div>

      <p className="text-xs text-brand-muted leading-relaxed mb-8 px-1">
        <span className="font-semibold text-brand-ink">Note: </span>
        {PROJECTION_DISCLAIMER}
      </p>

      <div>
        <button
          onClick={onContinue}
          className="w-full sm:w-auto px-8 py-3 rounded-xl bg-brand-green text-white font-semibold text-sm
                     hover:opacity-90 transition-opacity focus-visible:outline focus-visible:outline-2
                     focus-visible:outline-offset-2 focus-visible:outline-brand-green min-h-[44px]"
        >
          Got it — show me my dashboard
        </button>
      </div>
    </div>
  );
}

// ── Pillar badge ──────────────────────────────────────────────────────────────

const PILLAR_BADGE_CLASSES: Record<string, string> = {
  seo: 'bg-blue-50 text-blue-700',
  aeo: 'bg-violet-50 text-violet-700',
  geo: 'bg-emerald-50 text-emerald-700',
};

function PillarBadge({ pillar, label }: { pillar: string; label: string }) {
  return (
    <span
      className={`inline-block text-[10px] font-bold tracking-widest uppercase rounded px-2 py-0.5 ${PILLAR_BADGE_CLASSES[pillar] ?? ''}`}
    >
      {label}
    </span>
  );
}
